import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO, COMPANY } from "../../lib/content";
import { IMAGES } from "../../lib/images";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

/* three.js is code-split so the hero paints first and the 3D layer streams in */
const HeroThree = lazy(() => import("./HeroThree"));

const SCENE_MS = 5200;
export default function HeroSequence() {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [threeReady, setThreeReady] = useState(false);
  const sectionRef = useRef(null);
  // reduced motion -> render a single static poster frame, no crossfade layers at all
  const scenes = reduced ? IMAGES.hero.slice(0, 1) : IMAGES.hero;
  const activeIndex = reduced ? 0 : index;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    if (reduced) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % scenes.length), SCENE_MS);
    return () => clearInterval(timer);
  }, [reduced, scenes.length]);

  // mount the 3D layer just after first paint so it never delays the hero
  useEffect(() => {
    const idle = window.requestAnimationFrame(() => setThreeReady(true));
    return () => window.cancelAnimationFrame(idle);
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      aria-label="LYKIT Entertainment"
      className="relative isolate flex min-h-[84vh] items-end overflow-hidden bg-nearblack"
    >
      {/* cinematic crossfade sequence */}
      <motion.div className="absolute inset-0 z-0" style={reduced ? undefined : { y: parallaxY }}>
        {scenes.map((scene, i) => (
          <div
            key={scene.src}
            aria-hidden={i !== activeIndex}
            className="absolute inset-0"
            style={{
              opacity: i === activeIndex ? 1 : 0,
              transition: reduced ? "none" : "opacity 1500ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <img
              src={scene.src}
              alt=""
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
              className={`h-full w-full object-cover ${reduced ? "" : i % 2 === 0 ? "kb-a" : "kb-b"}`}
            />
          </div>
        ))}
      </motion.div>

      {/* overlay + vignette + grain */}
      <div
        className="grain absolute inset-0 z-10"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(14,13,20,0.74) 0%, rgba(14,13,20,0.58) 42%, rgba(14,13,20,0.86) 100%)",
        }}
      />
      <div
        className="absolute inset-0 z-10"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(1200px 620px at 42% 38%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.32) 68%, rgba(0,0,0,0.58) 100%)",
        }}
      />

      {/* soft brand glow that grounds the 3D logo */}
      <div
        className="absolute inset-0 z-[11]"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(760px 420px at 50% 52%, rgba(216,183,122,0.14) 0%, rgba(158,43,36,0.10) 42%, rgba(0,0,0,0) 72%)",
        }}
      />

      {/* three.js layer: rotating LYKIT logo + mouse-reactive particles */}
      {threeReady && (
        <Suspense fallback={null}>
          <HeroThree className="z-[12]" />
        </Suspense>
      )}

      {/* readability scrim so the headline always wins over the 3D layer */}
      <div
        className="absolute inset-0 z-[13]"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(100deg, rgba(14,13,20,0.82) 0%, rgba(14,13,20,0.66) 38%, rgba(14,13,20,0.34) 68%, rgba(14,13,20,0.2) 100%)",
        }}
      />

      {/* content */}
      <motion.div
        className="relative z-20 w-full pb-[clamp(40px,4.5vw,68px)] pt-[clamp(132px,15vh,188px)]"
        style={reduced ? undefined : { y: contentY, opacity: contentOpacity }}
      >
        <div className="lk-container-wide">
          <div className="max-w-[min(1180px,100%)]">
            <motion.div
              initial={{ opacity: 0, y: reduced ? 0 : 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-4"
            >
              <span aria-hidden="true" className="block h-[1px] w-10" style={{ background: "rgba(216,183,122,0.85)" }} />
              <span
                className="text-[10.5px] font-medium uppercase sm:text-[11.5px]"
                style={{ letterSpacing: "0.24em", color: "rgba(216,183,122,0.95)" }}
              >
                {COMPANY.legalName}
              </span>
            </motion.div>

            <h1 className="display-hero mt-6" style={{ color: "#FBFAF7" }} data-testid="hero-headline">
              {HERO.headline.map((line, i) => (
                <motion.span
                  key={line}
                  className={i === 3 ? "block editorial" : "block"}
                  initial={{ opacity: 0, y: reduced ? 0 : 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.85,
                    delay: reduced ? 0 : 0.12 + i * 0.09,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={i === 3 ? { color: "#E7D6B2", marginTop: "0.06em" } : undefined}
                >
                  {line}
                </motion.span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: reduced ? 0 : 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: reduced ? 0 : 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="body-large mt-7 max-w-[56ch]"
              style={{ color: "rgba(247,246,242,0.78)" }}
            >
              {HERO.supporting}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: reduced ? 0 : 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: reduced ? 0 : 0.64, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <Link to={HERO.primaryCta.to} data-testid="hero-explore-services-button" className="btn btn-ivory">
                {HERO.primaryCta.label}
              </Link>
              <Link to={HERO.secondaryCta.to} data-testid="hero-lets-collaborate-button" className="btn btn-on-dark">
                {HERO.secondaryCta.label}
              </Link>
            </motion.div>
          </div>

          {/* scene indicator */}
          <div className="mt-[clamp(28px,3.5vw,48px)] flex items-end justify-end gap-6">
            <div className="flex items-center gap-4" data-testid="hero-scene-indicator">
              <span
                className="hidden text-[10.5px] uppercase sm:block"
                data-testid="hero-scene-label"
                style={{ letterSpacing: "0.22em", color: "rgba(247,246,242,0.72)", minWidth: 130, textAlign: "right" }}
              >
                {scenes[activeIndex].label}
              </span>
              <div className="flex items-center gap-[6px]" aria-hidden="true">
                {scenes.map((scene, i) => (
                  <span
                    key={scene.label}
                    className="block h-[2px] transition-all duration-700"
                    style={{
                      width: activeIndex === i ? 26 : 12,
                      background: activeIndex === i ? "rgba(216,183,122,0.95)" : "rgba(247,246,242,0.28)",
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
